import { Group, Stack } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import { useAgentSession } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import {
  usePrComments,
  useUnifiedProcessGraph,
} from '../../../api/unified-process-graph-client/unified-process-graph-client.hooks';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { secondaryBase } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { H2, Text } from '../../../ui-library/typography/typography';
import { SessionPanel } from '../../agent-miner/session-panel/session-panel';
import { TurnPanel } from '../../agent-miner/turn-panel/turn-panel';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { ProcessOverviewTile } from '../process-overview';
import { PrCommentPanel } from './pr-comment-panel/pr-comment-panel';
import { UnifiedProcessGraphChart } from './unified-process-graph-chart';
import { UnifiedProcessGraphSidePanel } from './unified-process-graph-side-panel';
import { computeOverviewMetrics } from './unified-process-graph.helpers';

/**
 * Page for one task's unified process graph: its status changes, task events,
 * agent sessions and turns, and pull requests on one timeline.
 *
 * Responsibilities:
 * 1. Load the graph for the `:taskId` route param, with loading and error states
 * 2. Keep the selected session, turn or PR comment in the URL (`?session=`,
 *    `?turn=`, `?comment=`) so a refresh or shared link reopens the same panel
 * 3. Show one side panel at a time for the selection, collapsing Task Details
 *    to make room, and pass a `returnTo` so Agent Miner's Back comes here
 * 4. Fetch full comment bodies and PR details for an open comment panel
 *
 * @returns {JSX.Element} The graph page with its overview, Task Details and side panel
 */
export const UnifiedProcessGraph = () => {
  useDocumentTitle('Unified Process Graph - Bloomfilter');

  const { taskId } = useParams<{ taskId: string }>();
  const { data, query } = useUnifiedProcessGraph(taskId);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const selectedSessionId = searchParams.get('session');
  // `?turn=` only means something alongside its session.
  const selectedTurnId = selectedSessionId ? searchParams.get('turn') : null;
  // One side panel at a time: a session selection wins over a stray `?comment=`.
  const selectedCommentId = selectedSessionId ? null : searchParams.get('comment');
  // A deep link that opens a panel starts with Task Details collapsed, matching a click.
  const [showSideFilters, setShowSideFilters] = useState(() => !selectedSessionId && !selectedCommentId);
  // Shares SessionPanel's query key, so this adds no request. SessionPanel has
  // no close control while it has no data, so a failed fetch (deleted session,
  // no access, bad link) hides the panel instead of stranding an empty box.
  // TurnPanel keeps its back/close controls and shows its own error, so it stays.
  const { query: sessionQuery } = useAgentSession(selectedSessionId);

  const handleCloseSideFilters = useCallback(() => {
    setShowSideFilters(false);
  }, []);

  // The selection lives in the URL — `?session=` (same as Agent Miner), plus
  // `?turn=` for an open turn, or `?comment=` for a PR comment — so a refresh or
  // shared link reopens the panel. Each write replaces the whole selection, since
  // only one panel shows at a time. Opening any panel collapses Task Details to
  // give the graph room; the user can reopen it.
  const writeSelection = useCallback(
    (selection: { sessionId?: string; turnId?: string; commentId?: string }) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          const values: Record<string, string | undefined> = {
            session: selection.sessionId,
            turn: selection.sessionId ? selection.turnId : undefined,
            comment: selection.sessionId ? undefined : selection.commentId,
          };
          Object.entries(values).forEach(([key, value]) => {
            if (value) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          });
          return next;
        },
        { replace: true },
      );
      if (selection.sessionId || selection.commentId) setShowSideFilters(false);
    },
    [setSearchParams],
  );

  const handleSelectSession = useCallback((sessionId: string) => writeSelection({ sessionId }), [writeSelection]);
  const handleSelectTurn = useCallback(
    (sessionId: string, turnId: string) => writeSelection({ sessionId, turnId }),
    [writeSelection],
  );
  const handleSelectComment = useCallback((commentId: string) => writeSelection({ commentId }), [writeSelection]);
  const handleCloseSession = useCallback(() => writeSelection({}), [writeSelection]);
  const handleBackToSession = useCallback(
    () => writeSelection({ sessionId: selectedSessionId ?? undefined }),
    [writeSelection, selectedSessionId],
  );

  const header = (
    <Group>
      <Icon name="map" color={secondaryBase} size={36} />
      <H2>Process</H2>
    </Group>
  );

  const returnTo = `${location.pathname}${location.search}`;
  const task = data?.task;
  const graph = data?.graph;
  const selectedComment = selectedCommentId
    ? graph?.pr_comment_nodes.find((node) => node.id === selectedCommentId)
    : undefined;
  const selectedCommentPr = selectedComment
    ? graph?.scm_nodes.find((node) => node.id === selectedComment.change_request_id)
    : undefined;
  // The panel's arrows step through every comment on the PR, in time order.
  const selectedPrComments = selectedComment
    ? (graph?.pr_comment_nodes ?? [])
        .filter((node) => node.change_request_id === selectedComment.change_request_id)
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    : [];
  // Full comment bodies and PR details for the comment panel; the graph only has previews.
  const { data: prCommentDetails, query: prCommentsQuery } = usePrComments(taskId, selectedComment?.change_request_id);

  const metrics = useMemo(() => (graph ? computeOverviewMetrics(graph) : null), [graph]);

  if (query.isLoading) {
    return (
      <MenuDrivenContainer header={header}>
        <BrandedLoadingOverlay visible variant="colored" />
      </MenuDrivenContainer>
    );
  }

  if (query.isError) {
    return (
      <MenuDrivenContainer header={header}>
        <Stack align="center" justify="center" h="100%">
          <Text>Unable to load process graph for this task.</Text>
        </Stack>
      </MenuDrivenContainer>
    );
  }

  const mainSection = graph ? (
    <UnifiedProcessGraphChart
      data={graph}
      onSelectSession={handleSelectSession}
      onSelectTurn={handleSelectTurn}
      selectedTurnId={selectedTurnId}
      onSelectComment={handleSelectComment}
      selectedCommentId={selectedCommentId}
      selectedSessionId={selectedSessionId}
    />
  ) : (
    <Stack align="center" justify="center" h="100%">
      <Icon name="map" color={secondaryBase} size={48} />
      <Text size="large">Select a task to view its process graph</Text>
    </Stack>
  );

  const overview = metrics
    ? {
        title: 'Process Overview',
        content: (
          <Group grow gap={16}>
            <ProcessOverviewTile
              title="Cycle Time"
              description="First to last status transition"
              value={metrics.cycleTimeLabel}
            />
            <ProcessOverviewTile
              title="Agent Cost"
              description="Total cost across agent sessions"
              value={metrics.costLabel}
            />
            <ProcessOverviewTile
              title="Commits"
              description="Total commits across pull requests"
              value={metrics.commitsLabel}
            />
            <ProcessOverviewTile
              title="Code Changes"
              description="Lines added and removed"
              value={metrics.codeChangesLabel}
            />
          </Group>
        ),
      }
    : undefined;

  return (
    <MenuDrivenContainer header={header}>
      <PageLayout
        controlBar={null}
        overview={overview}
        sideFilters={{
          title: 'Task Details',
          content: <UnifiedProcessGraphSidePanel task={task} onClose={handleCloseSideFilters} />,
          show: showSideFilters,
          setShow: setShowSideFilters,
        }}
        mainSection={mainSection}
        sideSection={
          selectedCommentId ? (
            <PrCommentPanel
              comment={selectedComment}
              pr={selectedCommentPr}
              prComments={selectedPrComments}
              onSelectComment={handleSelectComment}
              onClose={handleCloseSession}
              details={prCommentDetails}
              isLoadingDetails={prCommentsQuery.isLoading}
              isDetailsError={prCommentsQuery.isError}
            />
          ) : selectedSessionId && selectedTurnId ? (
            <TurnPanel
              sessionId={selectedSessionId}
              turnId={selectedTurnId}
              onBack={handleBackToSession}
              onClose={handleCloseSession}
              returnTo={returnTo}
            />
          ) : (
            <SessionPanel sessionId={selectedSessionId} onClose={handleCloseSession} returnTo={returnTo} />
          )
        }
        showSideSection={
          !!graph && (!!selectedCommentId || (!!selectedSessionId && (!!selectedTurnId || !sessionQuery.isError)))
        }
      />
    </MenuDrivenContainer>
  );
};
